import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { useAuthStore } from '@/lib/auth/authStore';
import { ApiError } from './errors';

// ── Auth injection ─────────────────────────────────────────────────────

/**
 * Endpoints that are anonymous by nature: nothing to attach a token to, and a
 * 401 from them means "those credentials are wrong", not "your session died".
 *
 * The distinction matters more than it looks. A 401 anywhere else sends the
 * client down the refresh path and, when that fails, logs the user out and
 * redirects to /login. `/auth/register/*` was missing from this list, so
 * mistyping one digit of the signup code answered 401, was read as an expired
 * session, and threw the person out of the wizard onto the login page — for an
 * account that did not exist yet. The whole registration, lost to a typo.
 */
const AUTH_FREE_PATHS = [
  '/auth/login',
  '/auth/send-otp',
  '/auth/verify-otp',
  '/auth/check-email',
  '/auth/forgot-password',
  '/auth/refresh',
  // Prefix on purpose: every step of the signup wizard is anonymous, including
  // any step added later.
  '/auth/register/',
];

function shouldInjectAuth(url: string | undefined): boolean {
  if (!url) return true;
  return !AUTH_FREE_PATHS.some((p) => url.includes(p));
}

export function attachAuthInterceptor(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    if (shouldInjectAuth(config.url)) {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return config;
  });
}

// ── 401 → refresh → retry ──────────────────────────────────────────────

type RefreshFn = () => Promise<string | null>;

interface RefreshState {
  inFlight: Promise<string | null> | null;
}

const refreshState: RefreshState = { inFlight: null };

/**
 * Install a response interceptor that, on 401:
 *  1. Tries to refresh the access token (single-flight: parallel 401s share one refresh call).
 *  2. If refresh succeeds, retries the original request with the new token.
 *  3. If refresh fails, calls onAuthFailure (logout + redirect).
 *
 * The refresh endpoint (/auth/refresh) and login endpoints are themselves excluded
 * to avoid infinite loops.
 */
export function attachRefreshInterceptor(
  client: AxiosInstance,
  refresh: RefreshFn,
  onAuthFailure: () => void,
): void {
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const original = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined;

      const status = error.response?.status;
      const url = original?.url ?? '';
      const isAuthEndpoint = AUTH_FREE_PATHS.some((p) => url.includes(p));

      if (status === 401 && original && !original._retry && !isAuthEndpoint) {
        original._retry = true;

        // Single-session takeover: when the backend says this jti has been
        // superseded by a newer login, refreshing would race the OLD
        // session against the new one (sometimes "stealing" it back). Go
        // straight to logout so the previous tab lands on /login.
        const errBody = error.response?.data as
          | { code?: string }
          | undefined;
        if (errBody?.code === 'session-superseded') {
          onAuthFailure();
          throw ApiError.fromAxios(error);
        }

        if (!refreshState.inFlight) {
          refreshState.inFlight = refresh().finally(() => {
            refreshState.inFlight = null;
          });
        }

        const newToken = await refreshState.inFlight.catch(() => null);
        if (newToken) {
          original.headers.set('Authorization', `Bearer ${newToken}`);
          return client.request(original);
        }
        onAuthFailure();
      }

      throw ApiError.fromAxios(error);
    },
  );
}

// ── Retry with backoff for transient errors ────────────────────────────

const MAX_RETRIES = 3;
const BACKOFF_MS = [300, 800, 2000];

function isIdempotent(method: string | undefined): boolean {
  const m = (method ?? 'get').toLowerCase();
  return m === 'get' || m === 'head' || m === 'options';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry network errors, 5xx, and 429 (respecting Retry-After) on idempotent
 * methods. Runs BEFORE the refresh interceptor (must be installed last).
 *
 * Also reports server-side and network failures to Sentry once they're final
 * (retries exhausted, or non-retryable for non-idempotent methods). 4xx and
 * 401 are excluded — those are user/client errors handled elsewhere.
 */
export function attachRetryInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const config = error.config as
        | (InternalAxiosRequestConfig & { _retryCount?: number })
        | undefined;
      if (!config) throw error;

      const status = error.response?.status;
      const isNetwork = !error.response;
      const isServer = typeof status === 'number' && status >= 500 && status < 600;
      const isRateLimited = status === 429;
      const retriable = isNetwork || isServer || isRateLimited;

      if (retriable && isIdempotent(config.method)) {
        config._retryCount = (config._retryCount ?? 0) + 1;
        if (config._retryCount <= MAX_RETRIES) {
          let wait = BACKOFF_MS[config._retryCount - 1] ?? BACKOFF_MS[BACKOFF_MS.length - 1];
          if (isRateLimited) {
            const retryAfter = error.response?.headers?.['retry-after'];
            const seconds = retryAfter ? Number(retryAfter) : NaN;
            if (Number.isFinite(seconds) && seconds > 0) wait = seconds * 1000;
          }
          await delay(wait);
          return client.request(config);
        }
      }

      // Final failure — report network errors and 5xx to Sentry. Skip 4xx
      // (client/validation errors, 401 handled by the refresh interceptor).
      if (isNetwork || isServer) {
        Sentry.withScope((scope) => {
          scope.setTag('transport', 'http');
          scope.setTag('http.method', (config.method ?? 'get').toUpperCase());
          if (status !== undefined) scope.setTag('http.status_code', String(status));
          scope.setExtras({
            url: config.url,
            baseURL: config.baseURL,
            retries: config._retryCount ?? 0,
            isNetwork,
          });
          Sentry.captureException(error);
        });
      }

      throw error;
    },
  );
}

// ── Query param serialization (ox-platform list contract) ──────────────

/**
 * ox-platform expects list params shaped like the v1 react-admin dataProvider:
 *
 *   range  = JSON.stringify([start, end])
 *   sort   = JSON.stringify([field, order])
 *   filter = JSON.stringify({ ... })
 *
 * See: ox-xotski/src/dataProvider.ts. Frontend callers use a friendlier
 * shape ({ page, perPage, sort, order, filter }) which this helper converts.
 */
export interface ListParams {
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
}

export function buildListParams(params: ListParams = {}): Record<string, string> {
  const out: Record<string, string> = {};
  const page = params.page ?? 1;
  const perPage = params.perPage ?? 25;
  const start = (page - 1) * perPage;
  const end = start + perPage - 1;
  out.range = JSON.stringify([start, end]);
  if (params.sort) out.sort = JSON.stringify([params.sort, params.order ?? 'ASC']);
  if (params.filter && Object.keys(params.filter).length > 0) {
    out.filter = JSON.stringify(params.filter);
  }
  return out;
}
